/**
 * ResultStep Component
 *
 * Final step of the AI Briefing Wizard showing the generated caption
 * and optional image suggestions from Unsplash.
 */

import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui';
import { UnsplashImagePicker, type UnsplashImage } from '@/components/ui/UnsplashImagePicker';
import { ICONS } from '@/constants/icons';

interface ResultStepProps {
  generatedCaption: string;
  showImageSearch: boolean;
  initialImageQuery?: string;
  onSelectImage: (image: UnsplashImage) => void;
}

export const ResultStep = ({
  generatedCaption,
  showImageSearch,
  initialImageQuery = '',
  onSelectImage,
}: ResultStepProps) => {
  const { t } = useTranslation('ai');

  return (
    <div className="space-y-5 min-w-0">
      {/* Generated Caption Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center">
            <Icon name={ICONS.SPARKLES} size="xs" className="text-primary-600" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900">
            {t('ai_briefing_generated_caption', { defaultValue: 'Generated Caption' })}
          </h3>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
            {generatedCaption}
          </p>
        </div>
      </div>

      {/* Image Search Section */}
      {showImageSearch && (
        <div className="border-t border-gray-200 pt-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            {t('ai_briefing_image_suggestions_title', { defaultValue: 'Find an Image' })}
          </h3>
          <UnsplashImagePicker
            onSelectImage={onSelectImage}
            initialQuery={initialImageQuery}
            autoSearch={!!initialImageQuery}
            perPage={8}
            size="md"
          />
        </div>
      )}
    </div>
  );
};
